import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { getNameInitialsFromFullName } from '../../core/util/string';
import { BookingProfileStatus } from '../../constants';
import { fontFamily, fontSize } from '../../constants/text';
import { colors } from '../../constants/colors';

const StyledOption = styled.div.withConfig({ componentId: 'sc-fazvul-0' })<{ selected?: boolean }>`
  padding-left: 20px;
  padding-bottom: 10px;
  padding-top: 10px;
  border-top: 1px solid ${colors.borderLine};
  font-family: ${fontFamily.book};
  cursor: pointer;
  color: ${colors.blueMonday};
  &:hover {
    background-color: ${colors.whiteGrey};
    color: ${colors.blueMonday};
  }
  display: flex !important;
  justify-content: flex-start;
  align-items: center;
  ${(props) => css`
    background-color: ${props.selected && colors.blueJayWay};
    color: ${props.selected && 'white'};
  `}
  ${(props) => css`
    &:hover {
      background-color: ${props.selected && colors.blueJayWay};
      color: ${props.selected && 'white'};
    }
  `}
`;

const NameContainer = styled.div.withConfig({ componentId: 'sc-fazvul-1' })<{ color: { primary: string; secondary: string } }>`
  background-color: ${colors.blueJayWay};
  ${(props) => css`
    background-color: ${props.color.primary};
  `};
  border-radius: 50%;
  color: ${colors.white};
  height: 30px;
  margin-right: 15px;
  margin-top: 0;
  margin-bottom: 0;
  text-align: center;
  width: 30px;
  line-height: 30px;
  display: inline-block;
  font-size: ${fontSize.mediumSmall};
`;

const StyledPlusSign = styled.img.withConfig({ componentId: 'sc-fazvul-2' })`
  height: 20px;
  margin: 5px 15px 5px 2px;
  width: 20px;
  display: inline-block;
`;

type Props = {
  className?: string;
  isDisabled?: boolean;
  isFocused?: boolean;
  isSelected?: boolean;
  onFocus?: (...args: any[]) => any;
  onSelect?: (...args: any[]) => any;
  option: any;
  children: any;
};

const SelectOption = (props: Props) => {
  const { t } = useTranslation();
  const { option, onFocus, onSelect, isFocused } = props;

  const handleMouseDown = useCallback(
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();

      if (onSelect) {
        onSelect(option, event);
      }
    },
    [option, onSelect]
  );

  const handleMouseEnter = useCallback(
    (event: any) => {
      if (onFocus) {
        onFocus(option, event);
      }
    },
    [option, onFocus]
  );

  const handleMouseMove = useCallback(
    (event: any) => {
      if (isFocused) return;

      if (onFocus) {
        onFocus(option, event);
      }
    },
    [option, onFocus, isFocused]
  );

  const { isSelected, color, type } = props.option;
  const fullName =
    typeof props.children === 'string' ? props.children : props.children.props.children;

  return (
    <StyledOption
      className={props.className}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      selected={isSelected}
      title={props.option.title}
    >
      {type === BookingProfileStatus.EXISTS ? (
        <>
          <NameContainer color={color}>{getNameInitialsFromFullName(fullName, '')}</NameContainer>
          {fullName}
        </>
      ) : (
        <>
          <StyledPlusSign alt="plus-sign" src="/images/icons/plus-circle.svg" />
          {t('addNewFamilyMember')}
        </>
      )}
    </StyledOption>
  );
};

export default SelectOption;
